import {
    PsHttpService,
    type PatSnapCommonResponseData,
} from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { GetAgentsResponse } from '@/domains/home/apis/home-api';
import {
    type RdHomeProfilingRequest,
    type RdHomeProfilingResponse,
} from '@/domains/home/rd-home-profiling';

export interface TrizUploadParams {
    work_project_id: string;
    work_project_item_id: string;
}

export interface CreateTrizJobParams {
    message?: string;
    upload?: TrizUploadParams;
}

export interface SeekQueryCasesResponse {
    query_cases: string[];
}

export interface PlgHomeCase {
    title: string;
    description: string;
    role: string;
    industry: string;
    jtbd: string[];
    keywords: string[];
    html: { session_id: string; share_id: string };
    report: { session_id: string; share_id: string };
}

export interface HomeRdApi {
    getRecommendCases(): Promise<string[]>;
    getPlgHomeRecommendations(): Promise<string[]>;
    getPlgHomeCases(): Promise<PlgHomeCase[]>;
    getProfiling(params: RdHomeProfilingRequest): Promise<RdHomeProfilingResponse>;
    getRdCards(): Promise<GetAgentsResponse>;
    createTrizJob(params: CreateTrizJobParams): Promise<{ triz_mind_id: string; }>;
}

export const HomeRdApi = Symbol('HomeRdApi');

@injectable()
export class HomeRdApiImpl extends PsHttpService implements HomeRdApi {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}`,
            headers: {
                'X-API-Version': '1.0',
            },
        };
    }

    getRecommendCases(): Promise<string[]> {
        return this.get<PatSnapCommonResponseData<SeekQueryCasesResponse>>({
            url: '/eureka/rd-home-page/seek-query-cases',
            method: 'GET',
        }).then((res) => {
            const data = res?.data as SeekQueryCasesResponse | undefined;
            return data?.query_cases ?? [];
        });
    }

    getPlgHomeRecommendations(): Promise<string[]> {
        return this.get<PatSnapCommonResponseData<string[]>>({
            url: '/eureka/plg-home-page/recommend',
            method: 'GET',
        }).then((res) => Array.isArray(res.data) ? res.data : []);
    }

    getPlgHomeCases(): Promise<PlgHomeCase[]> {
        return this.get<PatSnapCommonResponseData<PlgHomeCase[]>>({
            url: '/eureka/plg-home-page/cases',
            method: 'GET',
        }).then((res) => Array.isArray(res.data) ? res.data : []);
    }

    getProfiling(params: RdHomeProfilingRequest): Promise<RdHomeProfilingResponse> {
        console.debug('[RD home profiling] requesting session profile', {
            inputLength: params.input.length,
        });
        return this.post<PatSnapCommonResponseData<RdHomeProfilingResponse>>({
            url: '/eureka/plg-home-page/session-profile',
            data: {
                input: params.input,
            },
        }).then((res) => res.data as RdHomeProfilingResponse);
    }

    getRdCards() {
        return this.get<PatSnapCommonResponseData<GetAgentsResponse>>({
            url: '/eureka/rd-home-page/rd-cards',
            method: 'GET',
        }).then((res) => {
            return res?.data as unknown as GetAgentsResponse;
        });
    }

    /**
     * 创建一个TRIZ任务
     */
    createTrizJob(params: CreateTrizJobParams) {
        return this.post<PatSnapCommonResponseData<{ triz_mind_id: string; }>>({
            url: '/eureka/triz-mind/create',
            data: params,
        }).then((res) => {
            return res.data as { triz_mind_id: string; };
        });
    }
}
